import { MatSort, SortDirection } from '@angular/material/sort';
import { MatTableDataSource as MatTableDataSource } from '@angular/material/table';
import { TMCClientParameters } from '@sdbn/tmc-common';
import { Component, OnInit, ViewChild, ViewEncapsulation, OnDestroy, Input } from '@angular/core';
import { interval, Subscription } from 'rxjs';
import { SelectionModel } from '@angular/cdk/collections';
import { AppClientConfiguration, ContextChange, WidgetStyle } from '@sdbn/common';
import { AbstractWidgetComponent } from '@sdbn/common';
import { ComponentSelectionStorageService } from '@sdbn/common';
import { TruckOverviewService } from '../truck.service';
import { Truck } from '../truck.model';
import { TruckOverviewFilterModel } from '../../truck-overview-filter/truck-overview.model';
import { AppClientConfigurationService } from '@sdbn/common';

const TABLE_SELECTION_KEY = 'trucks-overview.table.selection';

@Component({
  selector: 'app-truck-overview',
  templateUrl: './truck-overview.component.html',
  styleUrls: ['./truck-overview.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class TruckOverviewComponent extends AbstractWidgetComponent implements OnInit, OnDestroy {

  // controls if an initial load of transport must be performed. Must be executed only once!
  initialLoad = true;

  filterModel = new TruckOverviewFilterModel();

  // Table parameters
  dataSource = new MatTableDataSource(new Array<Truck>());
  dataSourceSubscription: Subscription;
  selection = new SelectionModel<Truck>();
  @ViewChild(MatSort, { static: true }) sort: MatSort;

  defaultVisibleColumns = ['driver', 'coDriver', 'carrier', 'supplier', 'numberPlate', 'registration'];

  columnsToDisplay: string[] = [];

  sortEmptyValuestoEndForColumns: string[] = ['start'];
  sortColumn = 'registration';
  sortDirection: SortDirection = 'asc' as SortDirection;
  // END Table parameters

  refreshEnabled = true;
  refreshInterval = 30000;
  refreshTimer: Subscription;

  constructor(
    private truckOverviewService: TruckOverviewService,
    private componentSelectionStorageService: ComponentSelectionStorageService,
    private configurationService: AppClientConfigurationService,
    private appClientConfiguration: AppClientConfiguration) {
    super();
    this.setupColumns();

    // the sorting based on ViewChild, needs to be initialized after view init because of *ngIf
    this.dataSource.sortingDataAccessor = (item: Truck, property) => {
      return item[property];
    };
    this.dataSource.data = [];
    this.dataSource.sortData = this.sortData.bind(this);
    // we need to subscribe to data source to react on changes in visible rows
    this.dataSourceSubscription = this.dataSource.connect().subscribe(
      () => this.onVisibleRowsChanged()
    );

    this.registerRefreshTimer();
  }

  ngOnInit() {

    // must be set in ngOnInit!
    this.dataSource.sort = this.sort;
    this.dataSource.sort.disableClear = true;

    /** The rule how the entered filter string is matched against rows' data values. */
    this.dataSource.filterPredicate = (data: Truck, filterString: string) => {

      if (!this.checkFulltextFilter(data, this.filterModel?.filterText)) {
        return false;
      }

      return true;
    };

  }

  ngOnDestroy() {

    super.ngOnDestroy();

    if (this.refreshTimer) {
      this.refreshTimer.unsubscribe();
    }
    if (this.dataSourceSubscription) {
      this.dataSourceSubscription.unsubscribe();
    }
  }

  private setupColumns() {
    const columnProperty = this.configurationService.getPropertyValue(TMCClientParameters.P_VISITS_TRUCKS_COLUMNS, null);
    if (columnProperty != null && String(columnProperty).length > 0) {

      const columProperties = String(columnProperty).split('|');
      for (const p of columProperties) {
        const checkColumn = this.defaultVisibleColumns.indexOf(p) !== -1;
        if (checkColumn) {
          this.columnsToDisplay.push(p);
        }
      }
    } else {
      this.columnsToDisplay.push(...this.defaultVisibleColumns);
    }
    this.columnsToDisplay.push('filler');
  }

  private sortData(data: Truck[], sort: MatSort) {
    if (!sort.active || sort.direction === '') {
      return data;
    }
    const isAsc = this.sort.direction === 'asc';

    const emptyToEnd = this.sortEmptyValuestoEndForColumns != null
      && this.sortEmptyValuestoEndForColumns.indexOf(sort.active) !== -1;

    return data.sort((a: Truck, b: Truck) => {

      // dedicated sort value first
      let value1 = a[sort.active + 'SortValue'];
      let value2 = b[sort.active + 'SortValue'];

      // default if no sort value is available
      if (value1 == null || value2 == null) {
        value1 = a[sort.active];
        value2 = b[sort.active];
      }

      if (emptyToEnd) {
        // Treat null or empty values always as greater
        if ((value1 == null || value1 === '') && (value2 == null || value2 === '')) {
          return 0;
        }
        if (value1 == null || value1 === '') {
          return +1;
        }
        if (value2 == null || value2 === '') {
          return -1;
        }
      }
      return this.compare(value1, value2, isAsc);
    });
  }

  private compare(a: number | string, b: number | string, isAsc: boolean) {
    let t = 0;
    if ((a == null || a === '') && (b == null || b === '')) {
      t = 0;
    } else if (a == null || a === '') {
      t = -1;
    } else if (b == null || b === '') {
      t = 1;
    } else {
      t = (a < b ? -1 : 1);
    }
    return t * (isAsc ? 1 : -1);
  }

  private checkFulltextFilter(data: Truck, filterString: string): boolean {
    const normalizedFilterString = filterString == null ? '' : filterString.toLowerCase();

    for (const k of Object.keys(data)) {
      const fieldValue = data[k];
      if (fieldValue != null) {
        const normalizedFieldValue = String(fieldValue).toLowerCase();
        if (normalizedFieldValue.indexOf(normalizedFilterString) > -1) {
          return true;
        }
      }
    }
    return false;
  }

  private registerRefreshTimer() {
    // TODO: before executing load, check if this page is visible. If we were on transport view, there is no need to load visitors.

    if (this.refreshTimer) {
      this.refreshTimer.unsubscribe();
    }

    if (this.appClientConfiguration.autoreload) {
      this.refreshTimer = interval(this.refreshInterval).subscribe(n => this.loadTrucks());
    } else {
      console.log('Autoreload visits disabled');
    }
  }

  public getHintClasses(visit: Truck): string[] {
    const hints: string[] = [];
    if (visit.hintStyle) {
      for (const s of visit.hintStyle.split(' ')) {
        hints.push(s);
      }
    }
    return hints;
  }

  public getRowStyles(visit: Truck) {
    const styles: string[] = [];
    if (this.selection.isSelected(visit)) {
      styles.push('selected');
    } else {
      styles.push('deselected');
    }
    if (visit.rowStyle != null) {
      styles.push(visit.rowStyle);
    }
    return styles;
  }

  public toggleSelection(truck: Truck) {
    this.selection.select(truck);
    this.dashboardContext.setContextValue('selected-truck', this.selection.isEmpty() ? null : truck);
    this.storeTableSelection();
  }

  private storeTableSelection() {
    this.componentSelectionStorageService.saveToSession(TABLE_SELECTION_KEY, this.selection.selected);
  }

  /**
   * Controls "no data" message in table if no rows are available
   */
  public showNoData(): boolean {
    return this.dataSource.filteredData.length === 0;
  }

  private onVisibleRowsChanged() {
    const autoselectEnabled = this.configurationService.isPropertyTrue(TMCClientParameters.P_VISITS_PERSONAL_VISIT_AUTO_SELECT_SINGLE_SEARCH_RESULT, false);
    if (autoselectEnabled && this.dataSource.filteredData.length === 1) {
      this.selection.select(this.dataSource.filteredData[0]);
    }
  }

  public loadTrucks() {
    this.truckOverviewService.getTrucks().subscribe(
      response => {
        this.dataSource.data = response;
        this.restoreTableSelection();
      },
      error => {
        this.dataSource.data = [];
        console.log('Error wihle Loading list of vists!', error);
      });
  }

  private restoreTableSelection() {
    this.selection.clear();
    const storedSelection: Truck[] = this.componentSelectionStorageService.getFromSession(TABLE_SELECTION_KEY);
    if (storedSelection) {
      const restoredSelection: Truck[] = [];
      for (const s of storedSelection) {
        for (const row of this.dataSource.data) {
          if (s.id === row.id) {
            restoredSelection.push(row);
          }
        }
      }
      this.selection.select(...restoredSelection);
      this.dashboardContext.setContextValue('selected-truck', restoredSelection[0]);
      this.storeTableSelection();
    }
  }

  private refreshBySelection() {

    this.filterModel = this.dashboardContext?.context.get('truck-selection');
    this.dataSource.filter = JSON.stringify(this.filterModel);

    // if the filter is set for the first time, an initial load is performed
    if (this.initialLoad) {
      this.initialLoad = false;
      this.loadTrucks();
    }
  }

  onContextChange(contextChange: ContextChange) {
    if (contextChange.key === 'truck-selection') {
      this.refreshBySelection();
    }
    if (contextChange.key === 'truck-changed') {
      this.loadTrucks();
    }
  }

  onSetElementAndContext() {
    this.refreshEnabled = this.widgetReference.refreshEnabled == null ? true : this.widgetReference.refreshEnabled;
    if (this.widgetReference.refreshInterval != null && this.widgetReference.refreshInterval > 0) {
      this.refreshInterval = this.widgetReference.refreshInterval * 1000;
    } else {
      this.refreshInterval = 30 * 1000;
    }
    this.registerRefreshTimer();

    this.refreshBySelection();
  }

  getStyle(): WidgetStyle {
    return WidgetStyle.transparent;
  }
}
